import "./RemoteControlPassive.css";
import { Box, TextField, Grid, Button } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment';
import { Machine } from "../../../../ts/Connections";
import { useState, useEffect } from 'react';
import { Cargo } from "../../../../ts/Cargo";
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SendIcon from '@mui/icons-material/Send';
import { AprilTag } from "../../../../ts/AprilTag";
import { useTranslation } from 'react-i18next'; // Import useTranslation hook
import Barcode_Mini_icon_White from '../../../../Assets/Barcode_Mini_Icon_White.png'
import { useAlert } from "../../../../AlertContext";

export enum PassiveState {
    READY = "READY",
    SCANNING = "SCANNING",
    UNCALIBRATED = "UNCALIBRATED",
    STALE = "STALE",
    ERROR = "ERROR",
}

interface RemoteControlPassiveProps {
    machine: Machine;
    cargoResponses: Cargo[];
    forklift: AprilTag;
    setOpenForkliftModal?: React.Dispatch<React.SetStateAction<boolean>>;
    user: string;
    pendingBarcode: string | null;
    setPendingBarcode: React.Dispatch<React.SetStateAction<string | null>>;
    scanOnEnter: boolean;
    onMachineSwap?: (barcode: string) => boolean;
}

const getButtonBackgroundColor = (status: PassiveState): string => {
    switch (status) {
        case PassiveState.SCANNING:
            return '#00004B'; // primary.main
        case PassiveState.UNCALIBRATED:
            return '#D3D3DE';
        case PassiveState.ERROR:
            return '#ff0f0f'; // error.main
        case PassiveState.STALE:
            return '#ff0f0f'; // stale uses error color
        case PassiveState.READY:
        default:
            return '#F7914B'; // secondary.main (awaiting scans)
    }
};

export default function RemoteControlPassive(props: RemoteControlPassiveProps) {
    const { machine, cargoResponses, forklift, setOpenForkliftModal, user, pendingBarcode, setPendingBarcode, scanOnEnter, onMachineSwap } = props;
    const [barcodeEntered, setBarcodeEntered] = useState<boolean>(false);
    const [barcodeValue, setBarcodeValue] = useState<string>("");
    const [lastProcessedCargoId, setLastProcessedCargoId] = useState<string | null>(null);
    const [machineStatus, setMachineStatus] = useState<PassiveState>(PassiveState.READY);
    const { t } = useTranslation(); // Initialize the translation function
    const { openAlert } = useAlert();

    useEffect(() => {
        if (cargoResponses.length > 0) {
            const latestCargo = cargoResponses[0];

            // Only process if this cargo hasn't been processed before (using a unique identifier)
            const cargoId = `${latestCargo.barcode}-${latestCargo.timeStamp || Date.now()}`;
            if (cargoId !== lastProcessedCargoId && (latestCargo.barcode === pendingBarcode)) {
                // Mark the cargo as matched
                latestCargo.pendingBarcodeMatched = true;

                // Check if there are dimension errors
                if (latestCargo.dimensionErrors && latestCargo.dimensionErrors.length > 0) {
                    // Create error message with all error codes and descriptions
                    const errorMessages = latestCargo.dimensionErrors.map(error =>
                        `${error.code}: ${error.description}`
                    ).join('\n');

                    openAlert(
                        'Rollthrough Scan Error',
                        `Barcode ${pendingBarcode} scanned with errors:\n\n${errorMessages}`,
                        'OK',
                        () => { },
                        false,
                        new Error('Dimension errors in matched barcode scan'),
                        {
                            component: "RemoteControlPassive",
                            action: "PendingBarcodeMatch",
                            barcode: pendingBarcode,
                            errors: latestCargo.dimensionErrors
                        },
                        'error'
                    );
                }

                setBarcodeEntered(false);
                setPendingBarcode(null);
            }

            // Update the last processed cargo ID if we have cargo
            if (cargoResponses.length > 0) {
                const currentCargoId = `${cargoResponses[0].barcode}-${cargoResponses[0].timeStamp || Date.now()}`;
                setLastProcessedCargoId(currentCargoId);
            }
        }
    }, [cargoResponses, pendingBarcode, setPendingBarcode, openAlert, t, lastProcessedCargoId]);

    useEffect(() => {
        let intervalId: ReturnType<typeof setInterval> | null = null;
        let apiUnknownCount = 0;
        const pollStatus = async () => {
            try {
                const resp: any = await machine.connection.get('status');

                // Check if the API is not recognized
                const statusCode = resp?.Responses?.status?.code as string | undefined;
                if (statusCode && statusCode.includes("API_UNKNOWN")) {
                    apiUnknownCount++;
                    if (apiUnknownCount >= 3) {
                        // Stop polling after 3 consecutive API_UNKNOWN responses
                        if (intervalId) {
                            clearInterval(intervalId);
                            intervalId = null;
                        }
                        return;
                    }
                    setMachineStatus(PassiveState.READY);
                    return;
                }

                // Reset counter on successful response
                apiUnknownCount = 0;

                const value = resp?.Responses?.Status?.value as string | undefined;
                const normalized = value ? value.toUpperCase() : undefined;
                if (normalized === PassiveState.READY || normalized === PassiveState.SCANNING || normalized === PassiveState.UNCALIBRATED || normalized === PassiveState.STALE) {
                    setMachineStatus(normalized as PassiveState);
                } else {
                    setMachineStatus(PassiveState.READY);
                }
            } catch (error) {
                // Reset counter on network errors
                apiUnknownCount = 0;
                setMachineStatus(PassiveState.ERROR);
            }
        };
        // Start immediately, then on interval
        pollStatus();
        intervalId = setInterval(pollStatus, 2000);
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [machine]);

    const styles = {
        barcodeComponent: {
            backgroundColor: '#FFFFFF',
            mt: 1
        },
        barcodeTextArea: {
            color: '#4B4E65',
            backgroundColor: '#EAEAF0',
        },
        barcodeAdornment: {
            fontSize: 20
        },
        forkliftName: {
            color: '#262D6D',
            fontWeight: 'medium',
            mt: 1,
            mb: 1,
            display: 'flex',
            alignItems: 'center'
        },
        forkliftNameValue: {
            color: '#F48430',
            fontWeight: 'bold',
            ml: 1,
            marginLeft: '8px'
        },
        forkliftBox: {
            backgroundColor: '#FFFFFF',
            textArea: {
                color: '#4B4E65',
                mt: 0.3
            },
        },
        passiveScanButton: {
            backgroundColor: getButtonBackgroundColor(machineStatus),
            opacity:
                machineStatus === PassiveState.UNCALIBRATED || machineStatus === PassiveState.STALE
                    ? 0.9
                    : machineStatus === PassiveState.READY
                        ? 0.7
                        : 1
        }
    }

    const submitBarcode = async (): Promise<void> => {
        // Handle "SPECTRE:<machineName>" swap barcodes the same way active mode does.
        if (onMachineSwap && onMachineSwap(barcodeValue)) {
            setBarcodeValue('');
            return;
        }
        try {
            setPendingBarcode(barcodeValue);
            setBarcodeEntered(true);
        
            
            if (forklift) {
                await machine.connection.post(commands.setBarcodeWithForklift());
            } else {
                await machine.connection.post(commands.setBarcode());
            }
            setBarcodeValue('');
        } catch (error) {
            console.error('Failed to process barcode', error);
            setBarcodeEntered(false);
            setPendingBarcode("");
        }
    };

    const onKeyDownHandler = async (
        event: React.KeyboardEvent<HTMLInputElement>
    ): Promise<void> => {
        const { key } = event;
        if (key === 'Enter' && scanOnEnter) {
            await submitBarcode();
        }
    };

    const commands: { setBarcode: Function; setBarcodeWithForklift: Function; } = {
        setBarcode: () => {
            return {
                "Requests": {
                    "SetBarcode": {
                        "Value": barcodeValue,
                    }
                }
            }
        },
        setBarcodeWithForklift: () => {
            return {
                "Requests": {
                    "SetScanInfo": {
                        "Tag": forklift.tagValue,
                        "Barcode": barcodeValue,
                        "OperatingUser": user,
                        "EnableForkliftName": true
                    }
                }
            }
        }
    }

    return (
        <>
            <Box>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-evenly"
                    alignItems="center"
                >
                    <Grid item md={11} xs={11} sx={styles.forkliftBox}>
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={forklift ? forklift.tagName : ""}
                            disabled
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment sx={styles.forkliftBox.textArea} position="start">
                                        {t('control.inputs.buttons.currentForklift')}:
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <Button
                                        onClick={() => setOpenForkliftModal && setOpenForkliftModal(true)}
                                        size="small"
                                        sx={{
                                            minWidth: '40px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px'
                                        }}
                                    >
                                        <SwapHorizIcon fontSize="small" />
                                    </Button>
                                )
                            }}
                        />
                    </Grid>
                    {barcodeEntered && pendingBarcode !== null && (
                        <Box className="pending-barcode-banner" role="status" aria-live="polite" sx={{
                            mb: 1.5,
                            mt: 1.5,
                            mx: 'auto',
                            p: 1.2,
                            width: '91%',
                            // Solid blue background per request, remove old border & gradient
                            backgroundColor: '#262D6D',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontSize: '1rem',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                        }}>
                            <img src={Barcode_Mini_icon_White} alt="barcode icon" style={{ width: 15, height: 10, marginRight: 8 }} />
                            {pendingBarcode ? t('control.inputs.rollthru.barcodeLoaded', { barcode: pendingBarcode }) : <>{t('control.inputs.rollthru.pending')}:&nbsp;<em>{t('control.inputs.rollthru.emptyBarcode')}</em></>}
                        </Box>
                    )}
                    <Grid item md={11} xs={11} sx={styles.barcodeComponent}>
                        <TextField id="barcode-field" variant="outlined" onKeyDown={onKeyDownHandler} fullWidth sx={styles.barcodeTextArea} value={barcodeValue} onChange={(element) => { setBarcodeValue(element.target.value) }}
                            InputProps={{
                                startAdornment: <InputAdornment sx={styles.barcodeAdornment} position="start">{t('control.inputs.barcode')} | </InputAdornment>,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button
                                            onClick={submitBarcode}
                                            size="small"
                                            sx={{
                                                minWidth: '40px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                color: '#262D6D'
                                            }}
                                        >
                                            <SendIcon fontSize="small" />
                                        </Button>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Grid item container
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                md={12}>
                <Grid item md={11} xs={11} pt={2}>
                    <Button
                        className={'Passive-Scan-Button'}
                        fullWidth
                        disabled={true}
                        sx={styles.passiveScanButton}
                    >
                        <span className="broadcasting" aria-hidden="true">
                            <span className="broadcast-dot"></span>
                            <span className="broadcast-wave"></span>
                            <span className="broadcast-wave"></span>
                        </span>
                        {t(`control.inputs.buttons.passiveButtonStates.${machineStatus?.toLowerCase()}`)}
                    </Button>
                </Grid>
            </Grid>
        </>
    );
}